import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { AlertCircle, CreditCard, Lock } from 'lucide-react'
import { getContentList } from '../../lib/api/content'
import { PageHead, Stat, StateChip } from './ui'
import { cn } from '../../lib/cn'

type El = Record<string, unknown>
const isYes = (v: unknown) => v === true || v === 'true' || v === 1 || v === '1'

type Row = { id: string; name: string; type: string; cta: string; enabled: boolean }

export function PaymentModesPage() {
  const list = useQuery({
    queryKey: ['content', 'paymentMode', 'all'],
    queryFn: () => getContentList('paymentMode', { limit: 100 }),
  })

  const rows = useMemo<Row[]>(() => {
    return (list.data?.data ?? [])
      .map((r) => {
        const el = r.elements as El
        return {
          id: r.id,
          name: String(el.name ?? el.label ?? 'Senza nome'),
          type: String(el.type ?? '').toUpperCase(),
          cta: String(el.ctaLabel ?? ''),
          enabled: isYes(el.enabled),
        }
      })
      .sort((a, b) => a.name.localeCompare(b.name, 'it'))
  }, [list.data])

  const stats = useMemo(() => ({ total: rows.length, active: rows.filter((r) => r.enabled).length }), [rows])

  return (
    <div className="w-full px-6 pb-16 pt-7 lg:px-9">
      <PageHead
        title="Configurazione · Metodi di pagamento"
        description="Modalità di pagamento accettate online e alla cassa, con la rispettiva call-to-action mostrata in fase di acquisto."
        model="paymentMode"
      />

      <div className="mb-[18px] grid grid-cols-2 gap-[14px] sm:grid-cols-3">
        <Stat label="Metodi" value={String(stats.total)} sub="configurati" />
        <Stat label="Attivi" value={String(stats.active)} sub="accettati" />
        <div className="cv-panel hidden items-center px-4 py-3.5 sm:flex">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-oro-soft px-2.5 py-1 text-[12px] font-bold text-oro-700">
            <Lock className="h-3.5 w-3.5" /> Sola lettura
          </span>
        </div>
      </div>

      {list.isError ? (
        <div className="cv-panel px-4 py-16 text-center text-sm text-muted">
          <AlertCircle className="mx-auto mb-2 h-5 w-5 text-venetian" /> Impossibile caricare i metodi di pagamento.
        </div>
      ) : list.isLoading ? (
        <div className="cv-panel divide-y divide-line-soft">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="px-5 py-4">
              <div className="skeleton h-4 w-48 rounded-sm bg-line-soft" />
            </div>
          ))}
        </div>
      ) : rows.length === 0 ? (
        <div className="cv-panel px-4 py-16 text-center text-sm text-muted">Nessun metodo di pagamento configurato.</div>
      ) : (
        <div className="cv-panel overflow-hidden">
          {rows.map((r) => (
            <div
              key={r.id}
              className={cn(
                'flex items-center gap-4 border-b border-line-soft px-5 py-4 last:border-0',
                !r.enabled && 'opacity-60',
              )}
            >
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-paper text-slate ring-1 ring-line">
                <CreditCard className="h-[18px] w-[18px]" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold text-coal">{r.name}</span>
                  {r.type && (
                    <span className="rounded-md bg-paper px-1.5 py-0.5 font-mono text-[10.5px] font-semibold tracking-wide text-muted ring-1 ring-line">
                      {r.type}
                    </span>
                  )}
                </div>
                {r.cta && <div className="mt-0.5 truncate text-[12.5px] text-muted">CTA: “{r.cta}”</div>}
              </div>
              <StateChip on={r.enabled} />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
